import { FC, useState } from 'react'
import styled from 'styled-components'
import { AAntDrawer } from 'views/components/Analytics'
import { UsContract, UsContractStatuses, UsContractTypes } from 'modules/domain/usContracts/types'
import { AntTag, Text } from '@agro-club/agroclub-shared'
import { Card } from 'views/components/Card/Card'
import { useTranslation } from 'react-i18next'
import useFormatDate from 'hooks/useFormatDate'
import { formatQuantity } from 'modules/utils/numbers/formatQuantity'
import { KeyValue } from 'views/components/KeyValue/KeyValue'
import { HrWithHeader } from 'views/components/Hr/Hr'
import { DASH } from 'modules/constants'
import { formatTarif } from 'modules/utils/numbers/formatTarif'
import { DatePeriodFormatted } from 'views/components/DatePeriodFormatted'
import { SliderTracker, SliderPaging } from 'views/components/Pagination/SpliderPaging'
import { UsContractEdit } from '../Modals/UsContractEdit'
import { CompanyLink } from 'views/components/CompanyLink/CompanyLink'
import { SignBlock } from './SignBlock'
import { CancelContractBtn } from './CancelContractBtn'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;

  height: 100%;
  overflow-y: auto;
`

const Tags = styled.div`
  display: flex;
  gap: 0 4px;
`
const Centered = styled.div`
  display: flex;
  justify-content: center;
`

type Props = {
  contract: UsContract
  refetch: (affected?: UsContractStatuses | 'allTabs') => Promise<void>
  onClose: () => void
}

export const UsContractDrawer: FC<Props> = ({ contract, onClose, refetch }) => {
  const { t } = useTranslation(['usContracts'])
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const formatDate = useFormatDate()
  const visiblePostion = contract.positions[currentPage - 1]
  return (
    <AAntDrawer id="UsContractDrawer" width={484} closable open placement="right" onClose={onClose}>
      <Wrapper>
        <div>
          <Card.GapableContent>
            <Centered>
              <Tags>
                {contract.contract_type === UsContractTypes.sale ? (
                  <AntTag color="blue">{t('contractTypes.sale')}</AntTag>
                ) : (
                  <AntTag color="purple">{t('contractTypes.purchase')}</AntTag>
                )}
                <AntTag color="white">{t(`contractStatuses.${contract.status}`)}</AntTag>
                <AntTag color="white">{t(`usPricingTypes.${contract.pricing_type}`)}</AntTag>
                <AntTag color="white">{formatDate(contract.contract_date)}</AntTag>
              </Tags>
            </Centered>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Centered>
                <Text typography="titleH3"> {formatQuantity(contract.calculated_quantity)}</Text>
              </Centered>
              <Centered>
                <Text typography="bodyLarge">{contract.product.title}</Text>
              </Centered>
            </div>
            <SignBlock contract={contract} refetch={refetch} />
            <KeyValue label={t('fields.responsible')}>{contract.sales_person.profile?.full_name}</KeyValue>
            <KeyValue label={t('fields.company')}>
              <div>
                <CompanyLink company={contract.company} />
              </div>
              <div>{contract.address?.short_address}</div>
            </KeyValue>
            <Card.Grid cols={2}>
              <KeyValue label={t('fields.delivery')}>{t(`deliveryTypes.${contract.delivery_type}`)}</KeyValue>
            </Card.Grid>
            <HrWithHeader text={t('positions')} />
            <Card.Container bordered padding="compact">
              <Card.Grid cols={2}>
                <KeyValue label={t('fields.basis')}>
                  {visiblePostion.basis ? formatTarif(visiblePostion.basis) : DASH}
                </KeyValue>
                <KeyValue label={t('fields.price')}>
                  {visiblePostion.price ? formatTarif(visiblePostion.price) : DASH}
                </KeyValue>
                <KeyValue label={t('fields.freight_cost')}>
                  {visiblePostion.freight_cost ? formatTarif(visiblePostion.freight_cost) : DASH}
                </KeyValue>
                <KeyValue label={t('fields.futures_price')}>
                  {visiblePostion.futures_price ? formatTarif(visiblePostion.futures_price) : DASH}
                </KeyValue>
                <KeyValue label={t('fields.quantity')}>{formatQuantity(visiblePostion.quantity)}</KeyValue>
                <KeyValue label={t('fields.symbol')}>{visiblePostion.symbol}</KeyValue>
                <KeyValue label={t('fields.transportMode')}>
                  {t(`usTransportModes.${visiblePostion.transport_mode}`)}
                </KeyValue>
                <KeyValue label={t('fields.pricing_type')}>
                  {t(`usPricingTypes.${visiblePostion.pricing_type}`)}
                </KeyValue>
                <KeyValue label={t('fields.deliveryPeriod')}>
                  <DatePeriodFormatted
                    period={{ start_date: visiblePostion.start_date, end_date: visiblePostion.end_date }}
                  />
                </KeyValue>
              </Card.Grid>
              <SliderPaging
                total={contract.positions.length}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
              />
            </Card.Container>
          </Card.GapableContent>
          <SliderTracker total={contract.positions.length} currentPage={currentPage} />
        </div>
        {!contract.cancelled_at && contract.status === UsContractStatuses.new && (
          <>
            <Card.EditButton onClick={() => setEditModalOpen(true)} />
            <CancelContractBtn contract={contract} onClose={onClose} refetch={refetch} />
          </>
        )}
      </Wrapper>
      {editModalOpen && (
        <UsContractEdit
          refetch={async () => {
            await refetch(contract.status)
          }}
          onClose={() => setEditModalOpen(false)}
          contract={contract}
        />
      )}
    </AAntDrawer>
  )
}
